import { z } from 'zod'

export const warningTierSchema = z.enum(['error', 'warning', 'info'])
export type WarningTier = z.infer<typeof warningTierSchema>

export const warningCodeSchema = z.enum([
  'date_order',
  'no_working_days',
  'book_window',
  'birthday_month',
  'birthday_already_used',
  'emergency_short_notice',
  'emergency_poor_planning',
  'unpaid_escalation',
  // Unpaid disallowed (per-gate flags, 2026-07-23): error tier, hard block at
  // submit when ALLOW_UNPAID_APPLICATION is off. Mirrors leave_rules.py R6.
  'unpaid_not_allowed',
  'blackout_overlap',
  // Annual/Unpaid blackout hard-block (per-gate flags): error tier emitted
  // instead of blackout_overlap when BLACKOUT_GATE_ESCALATE is off. R7.
  'blackout_blocked',
  // Annual/Sick leave during a blackout (Slice D): a soft "manager must
  // justify" signal emitted instead of blackout_overlap. Mirrors
  // services/api leave_rules.py R7.
  'blackout_requires_manager_justification',
  // Over-capacity discretionary application (capacity de-gated 2026-09-24):
  // warning tier, non-blocking. Manager decides at approval with an explicit
  // over-capacity confirm. Replaces the old team_slots_full block +
  // capacity_escalate route. R8.
  'capacity_over',
  'small_team_overlap',
  'teammate_overlap_info',
  'insufficient_balance',
  'maternity_gender',
  'paternity_gender',
  'family_probation',
  'marriage_probation',
  'replacement_no_grant',
  'replacement_expiry',
  'replacement_exceeds_grant',
  'documents_required',
  'balance_exceeded',
  'coverage_concern_for_health_leave',
])
export type WarningCode = z.infer<typeof warningCodeSchema>

export const warningSchema = z.object({
  tier: warningTierSchema,
  code: warningCodeSchema,
  message: z.string(),
})
export type Warning = z.infer<typeof warningSchema>

/** On-the-fly leave-pattern preview for the draft (public.pattern_catalog).
 *  Powers the Apply-page "Before you submit" pattern row. */
export const patternHitSchema = z.object({
  code: z.string(),
  displayName: z.string(),
  bucket: z.string(),
  /** This draft pushes the pattern over its threshold. */
  triggeredByDraft: z.boolean(),
  /** Already over the threshold before this request. */
  activeWithoutDraft: z.boolean(),
})
export type PatternHit = z.infer<typeof patternHitSchema>

export const leaveRuleResultSchema = z.object({
  warnings: z.array(warningSchema),
  computedDays: z.number().nonnegative(),
  canSubmit: z.boolean(),
  suggestedStatus: z.enum(['pending', 'escalated']),
  isEmergency: z.boolean(),
  managerName: z.string().nullable().optional(),
  patterns: z.array(patternHitSchema).optional(),
})
export type LeaveRuleResult = z.infer<typeof leaveRuleResultSchema>
